import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useClickAway } from 'ahooks';
import { getLocale, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';

import SvgIcon from '@home/components/SvgIcon';
import excelIcon from '@/pages/aigc/assets/svg/excel.svg';
import agentInstructions from '@/pages/aigc/assets/svg/agent-Instructions.svg';

import {
  parseTemplateToHtml,
  extractTextFromEditor,
  checkIfEdited,
  focusEditableAndMoveCursorToEnd,
  CSS_CLASSES,
} from '../AIPolishPopover/utils';
import { getColumnLetter } from '../SmartTableGrid/utils';
import './index.scss';

/**
 * AI列操作类型
 */
export enum AIColumnActionType {
  /** 修改本列数据条件 */
  MODIFY_CONDITION = 'modify_condition',
  /** 调整数据格式 */
  ADJUST_FORMAT = 'adjust_format',
}

/**
 * AI列操作模板配置 - i18n key 映射
 */
export const AI_COLUMN_TEMPLATE_KEYS: Record<AIColumnActionType, string> = {
  [AIColumnActionType.MODIFY_CONDITION]: 'dba_smart_table_ai_column_modify_condition_template',
  [AIColumnActionType.ADJUST_FORMAT]: 'dba_smart_table_ai_column_adjust_format_template',
};

interface IAIColumnPopoverProps {
  /** 是否可见 */
  visible: boolean;
  /** 操作类型 */
  actionType: AIColumnActionType;
  /** 当前表格名称 */
  tableName?: string;
  /** 当前工作表名称 */
  sheetName?: string;
  /** 当前操作的列索引 */
  colIndex?: number;
  /** 弹层位置 */
  position: { x: number; y: number; };
  /** 关闭回调 */
  onClose: () => void;
  /** 发送回调 */
  onSend: (prompt: string) => void;
}

/**
 * AI列操作弹层组件
 * 
 * 用于列右键菜单的"修改本列数据条件"和"调整数据格式"操作
 * 点击后弹出模板输入面板，与 AI润色当前表格 的交互一致
 */
const AIColumnPopover: React.FC<IAIColumnPopoverProps> = ({
  visible,
  actionType,
  tableName,
  sheetName,
  colIndex,
  position,
  onClose,
  onSend,
}) => {
  const { formatMessage } = useIntl();
  const defaultTableName = formatMessage({ id: 'dba_smart_table_table' });
  /** 用户是否已修改过【填写】区域 */
  const [hasEdited, setHasEdited] = useState<boolean>(false);
  /** 用于在原生事件回调中读取最新的 hasEdited 状态 */
  const hasEditedRef = useRef<boolean>(false);

  const popoverRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  
  /** 定时器引用，用于清理 */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * 清理定时器
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * 重置所有状态
   */
  const resetState = useCallback(() => {
    clearTimer();
    setHasEdited(false);
    hasEditedRef.current = false;
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  }, [clearTimer]);

  /**
   * 处理编辑器内容变化
   * - 只要用户曾经编辑过，就永久放开禁用，不会因改回默认值而重新禁用
   */
  const handleEditorInput = useCallback(() => {
    if (!editorRef.current) return;
    
    setHasEdited((prev) => {
      // 已经编辑过，无需再检查
      if (prev) return true;
      const edited = checkIfEdited(editorRef.current);
      if (edited) {
        hasEditedRef.current = true;
      }
      return edited;
    });
  }, []);

  /**
   * 处理可编辑区域的键盘事件（原生事件）
   */
  const handleEditableKeyDown = useCallback((e: KeyboardEvent) => {
    // Enter 键：阻止换行，执行发送
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!e.shiftKey) {
        setTimeout(() => {
          if (editorRef.current) {
            // 使用 ref 读取最新的 hasEdited 状态，只要曾经编辑过就允许发送
            const edited = hasEditedRef.current || checkIfEdited(editorRef.current);
            if (edited) {
              const prompt = extractTextFromEditor(editorRef.current);
              onSend(prompt);
              onClose();
            }
          }
        }, 0);
      }
      return;
    }
    
    // Escape 键：关闭弹层
    if (e.key === 'Escape') {
      onClose();
      return;
    }
  }, [onSend, onClose]);

  /**
   * 处理可编辑区域的粘贴事件（原生事件）
   */
  const handleEditablePaste = useCallback((e: ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain');
    if (!text) return;
    const cleanText = text.replace(/[\r\n]/g, ' ');
    document.execCommand('insertText', false, cleanText);
  }, []);

  /**
   * 清理编辑器上的事件监听器，防止监听器累积
   */
  const cleanupEditorListeners = useCallback(() => {
    if (!editorRef.current) return;
    const editableWrappers = editorRef.current.querySelectorAll(`.${CSS_CLASSES.EDITABLE_WRAPPER}`);
    editableWrappers.forEach((wrapper) => {
      wrapper.removeEventListener('input', handleEditorInput);
      wrapper.removeEventListener('keydown', handleEditableKeyDown as EventListener);
      wrapper.removeEventListener('paste', handleEditablePaste as EventListener);
    });
  }, [handleEditorInput, handleEditableKeyDown, handleEditablePaste]);

  /**
   * 初始化编辑器内容
   */
  const initEditor = useCallback(() => {
    if (!editorRef.current) return;

    // 先清理旧的监听器，防止重复添加
    cleanupEditorListeners();
    
    const templateKey = AI_COLUMN_TEMPLATE_KEYS[actionType];
    // 获取列字母
    const colLetter = colIndex !== undefined && colIndex >= 0 ? getColumnLetter(colIndex) : '';
    // 使用 i18n 参数传递，动态替换 sheetName 和 colLetter
    const template = formatMessage({ id: templateKey }, { sheetName: sheetName || '', colLetter });
    
    const html = parseTemplateToHtml(template);
    editorRef.current.innerHTML = html;
    
    // 为每个可编辑区域添加事件监听
    const editableWrappers = editorRef.current.querySelectorAll(`.${CSS_CLASSES.EDITABLE_WRAPPER}`);
    editableWrappers.forEach((wrapper) => {
      wrapper.addEventListener('input', handleEditorInput);
      wrapper.addEventListener('keydown', handleEditableKeyDown as EventListener);
      wrapper.addEventListener('paste', handleEditablePaste as EventListener);
    });
    
    // 聚焦第一个可编辑区域
    const firstEditable = editableWrappers[0] as HTMLElement;
    if (firstEditable) {
      focusEditableAndMoveCursorToEnd(firstEditable);
    }
  }, [actionType, colIndex, sheetName, handleEditorInput, handleEditableKeyDown, handleEditablePaste, cleanupEditorListeners]);

  /**
   * visible 变化时初始化
   */
  useEffect(() => {
    if (visible) {
      resetState();
      clearTimer();
      timerRef.current = setTimeout(() => {
        initEditor();
      }, 50);
    } else {
      resetState();
    }
  }, [visible, resetState, clearTimer, initEditor]);

  /**
   * 组件卸载时清理
   */
  useEffect(() => {
    return () => {
      clearTimer();
      cleanupEditorListeners();
    };
  }, [clearTimer, cleanupEditorListeners]);

  /**
   * 点击外部关闭弹层
   */
  useClickAway(
    () => {
      if (visible) {
        onClose();
      }
    },
    popoverRef,
    'mousedown'
  );

  /**
   * 判断是否可以发送
   */
  const canSend = hasEdited;

  /**
   * 处理发送
   */
  const handleSend = useCallback(() => {
    if (!canSend) return;
    const prompt = extractTextFromEditor(editorRef.current);
    onSend(prompt);
    onClose();
  }, [canSend, onSend, onClose]);

  /**
   * 计算弹层位置，确保不超出视口
   */
  const popoverPosition = useCallback(() => {
    const padding = 8;
    const popoverWidth = 700;
    const popoverHeight = 150;
    
    const x = Math.min(position.x, window.innerWidth - popoverWidth - padding);
    const y = Math.min(position.y, window.innerHeight - popoverHeight - padding);
    
    return { x: Math.max(padding, x), y: Math.max(padding, y) };
  }, [position]);

  if (!visible) return null;

  const locale = getLocale();
  const isEN = locale === LANGUAGE_EN;
  const { x, y } = popoverPosition();
  const displayName = tableName || defaultTableName;
  const colLetter = colIndex !== undefined && colIndex >= 0 ? getColumnLetter(colIndex) : '';
  // 构建显示的表格名称：表格名称 + 列区域信息（tableName 已包含 Sheet 名称）
  const colSuffix = colLetter
    ? (isEN ? ` Column ${colLetter}: ${colLetter} Area` : `的${colLetter}: ${colLetter}区域`)
    : '';
  const displayTableName = `${displayName}${colSuffix}`;
  const popoverContent = (
    <div
      className="dba-ai-column-popover"
      ref={popoverRef}
      style={{ left: x, top: y }}
    >
      {/* 头部区域（表格标识 + 输入框） */}
      <div className="dba-ai-column-popover__header-section">
        {/* 表格标识 */}
        <div className="dba-ai-column-popover__header">
          <img src={agentInstructions} alt="" className="dba-ai-column-popover__instructions-icon" />
          <div className="dba-ai-column-popover__container">
            <img src={excelIcon} alt="" className="dba-ai-column-popover__excel-icon" />
            <span className="dba-ai-column-popover__table-name">{displayTableName}</span>
          </div>
        </div>

        {/* 模板编辑区域 */}
        <div className="dba-ai-column-popover__input-wrapper">
          <div
            ref={editorRef}
            className="dba-ai-column-popover__template-editor"
          />
          <button
            className={`dba-ai-column-popover__send-btn ${!canSend ? 'dba-ai-column-popover__send-btn--disabled' : ''}`}
            onClick={handleSend}
            disabled={!canSend}
            type="button"
          >
            <SvgIcon type="ai-send" />
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popoverContent, document.body);
};

export default AIColumnPopover;
